import { Component, inject, Injectable } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';

interface ConfirmDialogData {
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
}

@Component({
  selector: 'app-confirm-dialog',
  standalone: true,
  imports: [MatDialogModule, MatButtonModule],
  template: `
    <h2 mat-dialog-title>{{ data.title }}</h2>
    <mat-dialog-content>{{ data.message }}</mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button [mat-dialog-close]="false">
        {{ data.cancelLabel }}
      </button>
      <button mat-flat-button color="primary" [mat-dialog-close]="true">
        {{ data.confirmLabel }}
      </button>
    </mat-dialog-actions>
  `,
})
export class ConfirmDialogComponent {
  readonly data = inject<ConfirmDialogData>(MAT_DIALOG_DATA);
}

/**
 * Thin confirmation-dialog wrapper, modeled after the management-portal's
 * `ConfirmationService` but reduced to a single yes/no use case (no confirmation-input
 * field, no icon/color config) since that's all the onboarding review flow needs.
 */
@Injectable({ providedIn: 'root' })
export class ConfirmationService {
  private readonly _dialog = inject(MatDialog);

  async confirm(data: Partial<ConfirmDialogData> = {}): Promise<boolean> {
    const dialogRef: MatDialogRef<ConfirmDialogComponent, boolean> =
      this._dialog.open(ConfirmDialogComponent, {
        disableClose: true,
        data: {
          title: data.title ?? 'Bitte bestätigen',
          message: data.message ?? 'Bist du sicher?',
          confirmLabel: data.confirmLabel ?? 'Bestätigen',
          cancelLabel: data.cancelLabel ?? 'Abbrechen',
        },
      });

    return (await firstValueFrom(dialogRef.afterClosed())) ?? false;
  }
}
